import { KeyboardEvent, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';

import './sliding-menu.scss';

export type SlidingMenuOption<T = any> = { //returned in passed `callback` prop
	label: string,
	data?: T,
	disabled?: boolean;
}

export type SlidingMenuProps = {
	options: SlidingMenuOption[];
	selectedOption: SlidingMenuOption | null;
	callback: (tab: SlidingMenuOption) => void;
	className?: string;
	fullWidthDivider?: boolean;
	/** tab ids are `${idPrefix}-tab-${index}`, controlling `${idPrefix}-panel` */
	idPrefix?: string;
	inert?: boolean;
}

export default function SlidingMenu(props: SlidingMenuProps) {
	const {
		fullWidthDivider = false,
		options,
		selectedOption,
		callback,
		idPrefix,
		inert,
	} = props;

	const isSelected = (option: SlidingMenuOption, index: number) => {
		return selectedOption ? selectedOption.label === option.label : index === 0;
	};

	const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
		const lastIndex = options.length - 1;
		const keyTargets: Record<string, number | undefined> = {
			ArrowRight: index === lastIndex ? 0 : index + 1,
			ArrowLeft: index === 0 ? lastIndex : index - 1,
			Home: 0,
			End: lastIndex,
		};
		const nextIndex = keyTargets[event.key];

		if (nextIndex !== undefined) {
			event.preventDefault();
			callback(options[nextIndex]);
			optionsWrapper.current?.querySelectorAll<HTMLElement>('[role="tab"]')[nextIndex]?.focus();
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			callback(options[index]);
		}
	};

	const [underlineStyle, setUnderlineStyle] = useState({ width: 0, left: 0 });
	const wrapper = useRef<HTMLDivElement>(null);
	const underline = useRef<HTMLDivElement>(null);
	const optionsWrapper = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!wrapper.current || !optionsWrapper.current) {
			return;
		}
		const selectedIndex = options.findIndex((option) => option.label === selectedOption?.label);
		const selectedElement = optionsWrapper.current.querySelector<HTMLElement>('.selected');
		if (![-1, 0].includes(selectedIndex)) {
			wrapper.current.classList.add('animate');
		}
		if (selectedElement) {
			setUnderlineStyle({
				width: selectedElement.offsetWidth ?? 0,
				left: selectedElement.offsetLeft ?? 0,
			});
			if (optionsWrapper.current.scrollWidth > wrapper.current.offsetWidth) {
				optionsWrapper.current.scroll({
					left: (selectedElement.offsetLeft + (selectedElement.offsetWidth / 2) - wrapper.current.offsetWidth) + (wrapper.current.offsetWidth / 2)
					, top: underline.current?.offsetHeight
					, behavior: 'smooth',
				});
			}
		}
	}, [options, selectedOption, optionsWrapper.current?.offsetWidth]);

	return (
		<div className={classNames('sliding-menu-wrapper', 'width-calc-full', props.className)} ref={wrapper} inert={inert}>
			<div className='sliding-menu-options position-relative' ref={optionsWrapper} role="tablist">
				{options.map((option: SlidingMenuOption, index: number) => {
					const selected = isSelected(option, index);

					return (
						<div
							key={index}
							onClick={() => callback(option)}
							onKeyDown={(event) => handleTabKeyDown(event, index)}
							role="tab"
							id={idPrefix ? `${idPrefix}-tab-${index}` : undefined}
							aria-controls={idPrefix ? `${idPrefix}-panel` : undefined}
							aria-selected={selected}
							aria-disabled={option.disabled || options.length === 1 || undefined}
							tabIndex={selected ? 0 : -1}
							className={classNames(
								'sliding-menu-option',
								'font-weight-heavy',
								'white-space-nowrap',
								'padding-medium',
								option.disabled || options.length === 1 ? 'disabled' : '',
								{ selected },
							)}
						>
							<span className='text'>{option.label}</span>
							{/* REMOVING 10/10/2023 based on comment from will but may return */}
							{/* {option.data?.tabCounter} */}
						</div>
					);
				})}
				
				<div className='sliding-menu-underline position-absolute' style={underlineStyle} ref={underline}></div>
			</div>
			{fullWidthDivider && <div className='sliding-menu-divider' />}
		</div>
	);
}